import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as Lucide from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID = `admin-${Math.random().toString(36).slice(2)}-${Date.now()}`;

export default function AdminAssistant({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const ask = async (text, displayText) => {
    const msg = (text ?? "").trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: displayText ?? msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/assistant`,
        { session_id: SID, message: msg },
        { headers }
      );
      setMessages((m) => [
        ...m,
        { role: "assistant", text: data.reply, quick_replies: data.quick_replies || [], proposal: data.proposal || null },
      ]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Bir sorun oluştu, lütfen tekrar deneyin." }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    ask("merhaba", "__skip__");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers });
      setMessages((m) => [...m, { role: "user", text: "Bir görsel yükledim.", image: data.url }]);
      await ask(`[GÖRSEL YÜKLENDİ] url: ${data.url}`, "__skip__");
    } catch (e) {
      toast.error("Görsel yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const applyProposal = async (idx) => {
    const msg = messages[idx];
    if (!msg?.proposal) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/assistant/apply`, { proposal: msg.proposal }, { headers });
      setMessages((m) => m.map((x, i) => (i === idx ? { ...x, applied: true } : x)));
      setMessages((m) => [...m, { role: "assistant", text: data.message || "İşlem tamamlandı.", done: true }]);
      toast.success(data.message || "Kaydedildi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem uygulanamadı");
    } finally {
      setLoading(false);
    }
  };

  const rejectProposal = (idx) => {
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, rejected: true } : x)));
    ask("Vazgeçtim, bu değişikliği yapma.");
  };

  return (
    <div data-testid="admin-assistant" className="mx-auto flex h-[calc(100vh-13rem)] max-w-3xl flex-col overflow-hidden rounded-3xl border border-charcoal/5 bg-cream-50 shadow-premium">
      <div className="flex items-center gap-3 border-b border-charcoal/5 bg-white px-6 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
          <Lucide.Sparkles className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div>
          <p className="font-serif text-lg text-charcoal-900">Yönetim Asistanı</p>
          <p className="text-xs text-charcoal-900/45">Paneli sizin için kullanır — foto ekler, metin düzeltir, özet çıkarır</p>
        </div>
      </div>

      <div ref={scrollRef} data-lenis-prevent className="flex-1 space-y-4 overflow-y-auto px-5 py-6">
        {messages.filter((m) => m.text !== "__skip__").map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
              <div
                data-testid={m.role === "user" ? "assistant-user-msg" : "assistant-bot-msg"}
                className={`whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "rounded-br-md bg-charcoal-900 text-cream-50"
                    : "rounded-bl-md border border-charcoal/5 bg-white text-charcoal-900"
                }`}
              >
                {m.image && <img src={m.image} alt="" className="mb-2 h-28 w-auto rounded-lg object-cover" />}
                {m.text}
              </div>

              {m.proposal && !m.applied && !m.rejected && (
                <div data-testid="assistant-proposal" className="mt-2 rounded-2xl border border-gold/30 bg-gold/5 p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold text-gold-dark">
                    <Lucide.CircleCheck className="h-4 w-4" strokeWidth={1.5} />
                    Onayınızı bekliyor
                  </p>
                  <p className="mt-1.5 text-sm text-charcoal-900/75">{m.proposal.summary}</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      data-testid="assistant-approve"
                      onClick={() => applyProposal(i)}
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-full bg-charcoal-900 px-5 py-2.5 text-xs font-semibold text-cream-50 transition-colors hover:bg-gold-dark disabled:opacity-50"
                    >
                      <Lucide.Check className="h-3.5 w-3.5" /> Onayla
                    </button>
                    <button
                      data-testid="assistant-reject"
                      onClick={() => rejectProposal(i)}
                      className="rounded-full border border-charcoal/15 px-5 py-2.5 text-xs font-medium text-charcoal-900/70 transition-colors hover:bg-charcoal/5"
                    >
                      Vazgeç
                    </button>
                  </div>
                </div>
              )}
              {m.applied && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                  <Lucide.CircleCheck className="h-3.5 w-3.5" /> Uygulandı
                </p>
              )}

              {m.role === "assistant" && m.quick_replies?.length > 0 && !m.proposal && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {m.quick_replies.map((q) => (
                    <button
                      key={q}
                      data-testid="assistant-quick-reply"
                      onClick={() => ask(q)}
                      className="rounded-full border border-charcoal/10 bg-white px-3.5 py-2 text-xs font-medium text-charcoal-900/75 transition-colors hover:border-gold/40 hover:text-gold-dark"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="flex gap-1 rounded-2xl border border-charcoal/5 bg-white px-4 py-3">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-900/40" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-900/40 [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-900/40 [animation-delay:300ms]" />
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-charcoal/5 bg-white px-4 py-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask(input);
          }}
          className="flex items-center gap-2"
        >
          <button
            type="button"
            data-testid="assistant-upload"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Görsel yükle"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-charcoal/10 text-charcoal-900/70 transition-colors hover:bg-charcoal/5 disabled:opacity-50"
          >
            {uploading ? <Lucide.Loader2 className="h-4 w-4 animate-spin" /> : <Lucide.ImagePlus className="h-4 w-4" strokeWidth={1.5} />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => uploadImage(e.target.files?.[0])}
          />
          <input
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Bir şey yazın... (örn. galeriye foto ekle, mesajları özetle)"
            className="flex-1 rounded-full border border-charcoal/10 bg-cream-100 px-4 py-2.5 text-sm text-charcoal-900 outline-none focus:border-gold/40"
          />
          <button
            data-testid="assistant-send"
            type="submit"
            disabled={loading || !input.trim()}
            aria-label="Gönder"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-charcoal-900 text-cream-50 transition-colors hover:bg-gold-dark disabled:opacity-40"
          >
            <Lucide.Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
